import React, { useCallback, useState, useEffect } from 'react';
import { Field, ErrorMessage } from '@atlaskit/form';
import Select from '@atlaskit/select';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { FieldContainer } from '@atlassian/jira-issue-create-commons/src/common/ui/fields/styled.tsx';
import { useSitePickerContext } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/controllers/use-site-picker-context/index.tsx';
import useFetchJiraAvailableSites from '../../../../services/fetch-jira-available-sites/index.tsx';
import messages from './messages.tsx';
import type { Props, SelectOption } from './types.tsx';

const SitePicker = (props: Props) => {
	const { classNamePrefix, onChange, onSiteSelection, width = 350 } = props;
	const [{ selectedCloudId }, { setSitePickerState }] = useSitePickerContext();
	const { sites, error, isLoading } = useFetchJiraAvailableSites();
	const { formatMessage } = useIntl();
	const [selectedValue, setSelectedValue] = useState({});

	useEffect(() => {
		if (!sites) return;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const currentSite = window.location.hostname.split('.')[0];
		const prefilledValue = sites?.filter((site) => site.label === currentSite)?.[0] || {};

		// if there's a cloudId that doesn't match host site, change selectedValue
		if (selectedCloudId && selectedCloudId !== prefilledValue?.value) {
			const selectedSite = sites?.filter((site) => site.value === selectedCloudId)?.[0] || {};

			// if no site is found in the list for any reason, default to host domain
			const initialValue = selectedSite || prefilledValue;
			setSelectedValue(initialValue);
			onSiteSelection?.(initialValue.value);
			return;
		}
		onSiteSelection?.(prefilledValue.value || '');

		setSitePickerState({
			siteDomain: prefilledValue.url || '',
		});
		setSelectedValue(prefilledValue);
	}, [onChange, selectedCloudId, onSiteSelection, setSitePickerState, sites]);

	const _onChange = useCallback(
		(option: SelectOption | null) => {
			setSitePickerState({
				siteDomain: option?.url || '',
				selectedCloudId: option?.value || '',
			});
			onChange?.();
			setSelectedValue(option || {});
			onSiteSelection?.(option?.value || '');
		},
		[setSitePickerState, onChange, onSiteSelection],
	);

	return (
		<ErrorBoundary>
			<ReportErrors packageName="jiraIssueCreate" id="JiraSitePickerSelect">
				<FieldContainer width={width}>
					<Field name="site" label={formatMessage(messages.sitePickerFieldLabel)}>
						{({ fieldProps }) => (
							<>
								<Select
									inputId={fieldProps?.id}
									value={selectedValue}
									options={sortSitesAlphabetically(sites)}
									// @ts-expect-error - TS2322 - Type '(option: SelectOption | null) => void' is not assignable to type '(value: Option | null, action: ActionMeta<Option>) => void'.
									onChange={_onChange}
									classNamePrefix={classNamePrefix}
									isLoading={isLoading}
								/>
								{error && <ErrorMessage>{formatMessage(messages.sitePickerError)}</ErrorMessage>}
							</>
						)}
					</Field>
				</FieldContainer>
			</ReportErrors>
		</ErrorBoundary>
	);
};

/**
 * Sorts sites alphabetically for SitePicker component. Exported for testing.
 * @param siteOptions list of sites, only need the label
 * @returns siteOptions alphabetically sorted
 */
export const sortSitesAlphabetically = (siteOptions: { label: string }[]) => {
	return siteOptions.sort((siteA, siteB) => {
		return siteA?.label.localeCompare(siteB?.label);
	});
};

export default SitePicker;
